import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, Files, HardDrive, Download as DownloadIcon, DollarSign,
  Search, Trash2, Ban, CheckCircle2, ShieldCheck, ChevronLeft, ChevronRight, Loader2,
} from "lucide-react";
import PageWrap from "@/fileshare/components/PageWrap";
import { useAuth } from "@/fileshare/context/AuthContext";
import api from "@/fileshare/lib/api";
import { formatBytes } from "@/fileshare/lib/format";
import { Button } from "@/fileshare/components/ui/button";
import { Input } from "@/fileshare/components/ui/input";
import { Badge } from "@/fileshare/components/ui/badge";
import { Skeleton } from "@/fileshare/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/fileshare/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/fileshare/components/ui/alert-dialog";

const TIER_BADGE = {
  free: "bg-slate-100 text-slate-600",
  plus: "bg-blue-50 text-blue-600",
  business: "bg-violet-50 text-violet-600",
};

const STAT_SKELETONS = ["adm-a", "adm-b", "adm-c", "adm-d", "adm-e"];

const StatCard = ({ icon: Icon, label, value, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4 }}
    className="bg-white border border-slate-200 rounded-lg p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
  >
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-500">{label}</span>
      <Icon className="h-4 w-4 text-slate-400" />
    </div>
    <p className="mt-3 text-2xl font-bold tracking-tight text-slate-900">{value}</p>
  </motion.div>
);

export default function Admin() {
  const { user: me } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const limit = 10;

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/overview");
      setOverview(data);
    } catch (e) {
      toast.error("Failed to load overview");
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", {
        params: { page, limit, ...(query ? { search: query } : {}) },
      });
      setUsers(data.users);
      setTotal(data.total);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const patchUser = async (u, body, okMsg) => {
    setBusyId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, body);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success(okMsg);
      loadOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (u) => {
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      loadUsers();
      loadOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <PageWrap className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-center gap-2.5 mb-1">
        <ShieldCheck className="h-6 w-6 text-blue-500" />
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="admin-title">
          Admin Panel
        </h1>
      </div>
      <p className="text-sm text-slate-500 mb-8">Manage accounts, tiers and platform health.</p>

      {/* Overview stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
        {overview ? (
          <>
            <StatCard icon={Users} label="Users" value={overview.total_users.toLocaleString()} delay={0} />
            <StatCard icon={Files} label="Active files" value={overview.active_files.toLocaleString()} delay={0.05} />
            <StatCard icon={HardDrive} label="Storage" value={formatBytes(overview.storage_bytes)} delay={0.1} />
            <StatCard icon={DownloadIcon} label="Downloads" value={overview.total_downloads.toLocaleString()} delay={0.15} />
            <StatCard icon={DollarSign} label="Revenue" value={`$${overview.revenue.toFixed(2)}`} delay={0.2} />
          </>
        ) : (
          STAT_SKELETONS.map((k) => <Skeleton key={k} className="h-[104px] rounded-lg" />)
        )}
      </div>

      {/* Users table */}
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900">Accounts ({total})</h2>
        <form onSubmit={submitSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email or name…"
              className="pl-9 w-64"
            />
          </div>
          <Button type="submit" size="sm" variant="outline" data-testid="admin-search-btn">Search</Button>
        </form>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-users-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60 text-left">
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">User</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Tier</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Files</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Storage</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center">
                  <Loader2 className="h-5 w-5 text-blue-500 animate-spin inline-block" />
                </td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-500" data-testid="admin-empty">
                  No accounts found.
                </td></tr>
              ) : (
                users.map((u) => {
                  const isSelf = me && u.id === me.id;
                  const locked = u.is_admin || isSelf;
                  return (
                    <tr key={u.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/40" data-testid={`admin-user-row-${u.email}`}>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                          {u.display_name || u.email.split("@")[0]}
                          {u.is_admin && <ShieldCheck className="h-3.5 w-3.5 text-blue-500" />}
                        </p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <Select
                          value={u.tier}
                          disabled={busyId === u.id}
                          onValueChange={(t) => t !== u.tier && patchUser(u, { tier: t }, `${u.email} → ${t}`)}
                        >
                          <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`admin-tier-select-${u.email}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="plus">Plus</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{u.file_count}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{formatBytes(u.storage_used || 0)}</td>
                      <td className="px-5 py-3.5">
                        {u.is_suspended ? (
                          <Badge className="bg-red-50 text-red-600 hover:bg-red-50">Suspended</Badge>
                        ) : (
                          <Badge className={`${TIER_BADGE[u.tier] || TIER_BADGE.free} hover:bg-inherit`}>Active</Badge>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            data-testid={`admin-suspend-btn-${u.email}`}
                            variant="ghost"
                            size="sm"
                            disabled={locked || busyId === u.id}
                            className={u.is_suspended ? "text-emerald-600" : "text-amber-600"}
                            onClick={() => patchUser(u, { is_suspended: !u.is_suspended },
                              u.is_suspended ? `${u.email} unsuspended` : `${u.email} suspended`)}
                            title={u.is_suspended ? "Unsuspend" : "Suspend"}
                          >
                            {u.is_suspended ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                data-testid={`admin-delete-btn-${u.email}`}
                                variant="ghost"
                                size="sm"
                                disabled={locked || busyId === u.id}
                                className="text-red-500 hover:text-red-600"
                                title="Delete account"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete {u.email}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This permanently removes the account, all uploaded files, batches and file requests. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  data-testid="admin-delete-confirm"
                                  className="bg-red-500 hover:bg-red-600"
                                  onClick={() => deleteUser(u)}
                                >
                                  Delete account
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button data-testid="admin-prev-page" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button data-testid="admin-next-page" variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageWrap>
  );
}
